import { z } from "zod";
import type { EmpruntNonResidentFormStep } from "./ui-state.js";

const numberLikeSchema = z.union([z.string(), z.number()]).optional();
const consentSchema = z
  .boolean()
  .refine((value) => value, "Le consentement est obligatoire.");

export const empruntNonResidentIntroStepSchema = z.object({});

export const empruntNonResidentProfilStepSchema = z.object({
  paysResidence: z.string().trim().min(1, "Le pays de residence est obligatoire.").max(120),
  statutProfessionnel: z.string().trim().min(1, "Le statut professionnel est obligatoire.").max(160),
  revenuMensuel: z.string().trim().min(1, "La tranche de revenus est obligatoire.").max(120),
  apportDisponible: z.string().trim().min(1, "Le niveau d'apport est obligatoire.").max(120),
  garantie: z.enum(["Oui", "Non"], {
    message: "La garantie est obligatoire.",
  }),
});

export const empruntNonResidentFinancesStepSchema = z
  .object({
    chargesMensuelles: numberLikeSchema,
    revenuCapital: numberLikeSchema,
    dettesAssociees: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredNonNegative("chargesMensuelles", input.chargesMensuelles, ctx);
    validateOptionalNonNegative("revenuCapital", input.revenuCapital, ctx);
    validateOptionalNonNegative("dettesAssociees", input.dettesAssociees, ctx);
  });

export const empruntNonResidentContactStepSchema = z.object({
  email: z.string().email("Email invalide."),
  firstName: z.string().trim().max(120, "Le prenom est trop long."),
  source: z
    .string()
    .trim()
    .min(1, "La source est obligatoire.")
    .max(120, "La source est trop longue."),
  consent: consentSchema,
  honeypot: z.string().max(0, "Requete invalide.").optional(),
});

export const empruntNonResidentFormSchema = z
  .object({
    email: z.string().email("Email invalide."),
    firstName: z.string().trim().max(120, "Le prenom est trop long."),
    source: z
      .string()
      .trim()
      .min(1, "La source est obligatoire.")
      .max(120, "La source est trop longue."),
    consent: consentSchema,
    honeypot: z.string().max(0).optional(),
    paysResidence: z.string().trim().min(1).max(120),
    statutProfessionnel: z.string().trim().min(1).max(160),
    revenuMensuel: z.string().trim().min(1).max(120),
    chargesMensuelles: numberLikeSchema,
    apportDisponible: z.string().trim().min(1).max(120),
    garantie: z.enum(["Oui", "Non"]),
    revenuCapital: numberLikeSchema,
    dettesAssociees: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredNonNegative("chargesMensuelles", input.chargesMensuelles, ctx);
    validateOptionalNonNegative("revenuCapital", input.revenuCapital, ctx);
    validateOptionalNonNegative("dettesAssociees", input.dettesAssociees, ctx);
  });

export const empruntNonResidentStepSchemas: Record<EmpruntNonResidentFormStep, z.ZodTypeAny> = {
  intro: empruntNonResidentIntroStepSchema,
  profil: empruntNonResidentProfilStepSchema,
  finances: empruntNonResidentFinancesStepSchema,
  contact: empruntNonResidentContactStepSchema,
  result: z.object({}),
};

function parseNumberLike(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const normalized =
    typeof value === "number"
      ? value
      : Number(value.replaceAll(/\s+/g, "").replace(",", "."));
  return Number.isFinite(normalized) ? normalized : Number.NaN;
}

function validateRequiredNonNegative(
  fieldName: string,
  value: string | number | undefined,
  ctx: z.RefinementCtx,
): void {
  if (value === undefined || value === null || (typeof value === "string" && !value.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` est obligatoire.`,
    });
    return;
  }
  const parsed = parseNumberLike(value);
  if (parsed === undefined || !Number.isFinite(parsed) || parsed < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif ou nul.`,
    });
  }
}

function validateOptionalNonNegative(
  fieldName: string,
  value: string | number | undefined,
  ctx: z.RefinementCtx,
): void {
  if (value === undefined || value === null || value === "") return;
  const parsed = parseNumberLike(value);
  if (parsed === undefined || !Number.isFinite(parsed) || parsed < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif ou nul.`,
    });
  }
}
